import { getAccessToken } from '../util/http'
import { socketBaseUrl } from '../util/env'
import diagnosticsManager from './diagnostics'

const SOCKET_STATE = {
  IDLE: 'idle',
  CONNECTING: 'connecting',
  OPEN: 'open',
  CLOSING: 'closing',
  RECONNECTING: 'reconnecting'
}

const EVENT_NAMES = ['open', 'close', 'message', 'error', 'statechange', 'reconnecting', 'reconnect', 'snapshot']

const DEFAULT_OPTIONS = {
  heartbeatInterval: 30000,
  heartbeatTimeout: 45000,
  connectTimeout: 15000,
  autoReconnect: true,
  maxQueueLength: 100
}

function now() {
  return Date.now()
}

function sanitizeUrl(url) {
  if (!url) return url
  return url.replace(/(token=)[^&]+/i, '$1***')
}

function backoffDelay(attempt) {
  const base = 1000
  const max = 15000
  const jitter = Math.random() * 0.3 + 0.85
  return Math.min(base * Math.pow(1.8, attempt), max) * jitter
}

class SocketClient {
  constructor(options = {}) {
    const {
      heartbeatInterval = DEFAULT_OPTIONS.heartbeatInterval,
      heartbeatTimeout = DEFAULT_OPTIONS.heartbeatTimeout,
      connectTimeout = DEFAULT_OPTIONS.connectTimeout,
      autoReconnect = DEFAULT_OPTIONS.autoReconnect,
      maxQueueLength = DEFAULT_OPTIONS.maxQueueLength,
      urlBuilder,
      diagnostics
    } = options

    this.config = { heartbeatInterval, heartbeatTimeout, connectTimeout, autoReconnect, maxQueueLength }
    this.state = SOCKET_STATE.IDLE
    this.socket = null
    this.connectPromise = null
    this.heartbeatTimer = null
    this.reconnectTimer = null
    this.connectTimeoutTimer = null
    this.retryCount = 0
    this.lastSeen = 0
    this.intentionalClose = false
    this.usesDefaultUrlBuilder = !urlBuilder
    this.urlBuilder = urlBuilder || (() => `${socketBaseUrl}ws?token=${getAccessToken()}`)
    this.diagnostics = diagnostics
    this.listeners = EVENT_NAMES.reduce((acc, name) => {
      acc[name] = new Set()
      return acc
    }, {})
    this.pendingQueue = []
  }

  /**
   * 建立或复用 WebSocket 连接。
   * - 无 token 且使用默认 urlBuilder 时会拒绝连接。
   * - 重复调用会复用现有连接或返回进行中的 Promise。
   * @param {boolean} force 是否强制重新建立连接（用于重连流程）
   * @returns {Promise<WebSocket>}
   */
  connect(force = false) {
    if (this.usesDefaultUrlBuilder && !getAccessToken()) {
      const error = new Error('缺少会话令牌，无法建立连接')
      error.code = 'NO_SESSION_TOKEN'
      this.diagnostics.warn('connect.skipNoToken', { state: this.state })
      this._clearReconnectTimer()
      this.connectPromise = null
      this.socket = null
      this._setState(SOCKET_STATE.IDLE, { reason: 'noToken' })
      return Promise.reject(error)
    }

    if (!force) {
      if (this.isConnected()) {
        this.diagnostics.debug('connect.skipAlreadyOpen', { state: this.state })
        return Promise.resolve(this.socket)
      }
      if (this.state === SOCKET_STATE.CONNECTING && this.connectPromise) {
        this.diagnostics.debug('connect.skipInFlight', { state: this.state })
        return this.connectPromise
      }
      if (this.state === SOCKET_STATE.RECONNECTING && this.connectPromise) {
        this.diagnostics.debug('connect.skipReconnecting', { state: this.state })
        return this.connectPromise
      }
    }

    this._clearReconnectTimer()
    this.intentionalClose = false
    const nextState = this.state === SOCKET_STATE.RECONNECTING ? SOCKET_STATE.RECONNECTING : SOCKET_STATE.CONNECTING
    this._setState(nextState, { reason: force ? 'force' : 'manual' })

    this.connectPromise = new Promise((resolve, reject) => {
      let url
      try {
        url = this.urlBuilder()
      } catch (builderError) {
        this.diagnostics.error('connect.urlBuilderError', { error: builderError })
        this.connectPromise = null
        this._notifySnapshot('connect.urlBuilderError', { error: builderError })
        reject(builderError)
        return
      }

      const safeUrl = sanitizeUrl(url)
      this.diagnostics.info('connect.attempt', { state: this.state, force, url: safeUrl })

      let socket
      try {
        socket = new WebSocket(url)
      } catch (error) {
        this.diagnostics.error('connect.failed', { error, url: safeUrl })
        this.connectPromise = null
        this._emit('error', error)
        this._notifySnapshot('connect.failed', { error })
        reject(error)
        if (this._shouldAutoReconnect()) this._scheduleReconnect()
        return
      }

      this.socket = socket
      this.lastSeen = now()
      this._notifySnapshot('connect.pending')

      let openHandled = false
      let closeHandled = false

      const handleOpen = () => {
        if (openHandled) return
        openHandled = true
        this._clearConnectTimeout()
        const attempt = this.retryCount
        this.retryCount = 0
        this.connectPromise = null
        this._setState(SOCKET_STATE.OPEN, { attempt, reconnect: attempt > 0 })
        this.diagnostics.info('connect.open', {
          attempt,
          reconnect: attempt > 0,
          queueLength: this.pendingQueue.length
        })
        this._emit('open', socket)
        if (attempt > 0) {
          this._emit('reconnect', { attempt })
        }
        this._startHeartbeat()
        this._flushQueue()
        this._notifySnapshot('connect.open', { attempt, reconnect: attempt > 0 })
        resolve(socket)
      }

      const handleMessage = (event) => {
        const data = event?.data
        this.lastSeen = now()
        const isPong = data === 'pong'
        this.diagnostics.debug('message.in', {
          type: isPong ? 'pong' : 'data',
          data: isPong ? data : JSON.parse(data ?? '{}')
        })
        if (!isPong) {
          this._emit('message', event)
        }
        this._notifySnapshot('message', { type: isPong ? 'pong' : 'data' })
      }

      const scheduleCloseFallback = (event = {}) => {
        if (closeHandled) return
        setTimeout(() => {
          if (closeHandled) return
          this.diagnostics.warn('connection.closeFallback', {
            code: event.code,
            reason: event.reason
          })
          handleClose({
            ...event,
            code: event.code ?? 1011,
            reason: event.reason ?? 'fallback close',
            wasClean: false,
            fallback: true
          })
        }, 0)
      }

      const handleError = (error) => {
        this.diagnostics.error('socket.error', { error })
        this._clearConnectTimeout()
        this._emit('error', error)
        if (this.socket && !this.intentionalClose) {
          try {
            this.socket.close(4006, 'error fallback')
          } catch (closeError) {
            this.diagnostics.error('socket.errorForceCloseFailed', { error: closeError })
          }
          scheduleCloseFallback({ code: 4006, reason: 'error fallback' })
        }
      }

      const handleClose = (event = {}) => {
        if (closeHandled) return
        closeHandled = true
        this._clearConnectTimeout()
        this.diagnostics.info('connection.closed', {
          code: event.code,
          reason: event.reason,
          clean: event.wasClean,
          intentional: this.intentionalClose,
          hadOpenState: this.state === SOCKET_STATE.OPEN
        })

        this._stopHeartbeat()
        this.connectPromise = null
        this.socket = null

        const prevIntentional = this.intentionalClose
        this.intentionalClose = false

        this._setState(SOCKET_STATE.IDLE, {
          code: event.code,
          intentional: prevIntentional
        })
        this._emit('close', event)

        if (!prevIntentional && this._shouldAutoReconnect()) {
          this._scheduleReconnect()
        }
        this._notifySnapshot('connection.closed', {
          code: event.code,
          intentional: prevIntentional
        })
      }

      socket.addEventListener('open', handleOpen)
      socket.addEventListener('message', handleMessage)
      socket.addEventListener('error', handleError)
      socket.addEventListener('close', handleClose)
      this._startConnectTimeout()
    })

    return this.connectPromise
  }

  /**
   * 主动关闭连接。
   * 默认禁用后续重连并清空等待队列，可通过 options 控制行为。
   * @param {object} options
   * @param {boolean} [options.disableReconnect=true] 关闭后是否禁止自动重连
   * @param {boolean} [options.clearQueue=true] 是否清空待发送队列
   * @param {number} [options.code] 关闭码
   * @param {string} [options.reason] 关闭原因
   */
  close(options = {}) {
    const { disableReconnect = true, clearQueue = true, code, reason } = options
    if (!this.socket) {
      this.diagnostics.debug('close.noop', { state: this.state })
      return
    }

    if (disableReconnect) {
      this.config.autoReconnect = false
    }
    if (clearQueue) {
      this.pendingQueue = []
    }

    this.intentionalClose = true
    this.retryCount = 0
    this._clearReconnectTimer()
    this._stopHeartbeat()
    this._clearConnectTimeout()
    this._setState(SOCKET_STATE.CLOSING, { code, reason })
    this.diagnostics.info('close.request', { code, reason, disableReconnect })

    try {
      if (typeof code === 'number') {
        this.socket.close(code, reason)
      } else {
        this.socket.close()
      }
    } catch (error) {
      this.diagnostics.error('close.error', { error })
    }
    this._notifySnapshot('close.request', { code, reason, disableReconnect })
  }

  /**
   * 发送消息。
   * 若当前未连上，消息将根据配置排队，必要时触发连接。
   * @param {string|object} raw 字符串或可序列化对象
   * @returns {boolean} 是否成功发出（排队返回 false）
   */
  send(raw) {
    const payload = typeof raw === 'string' ? raw : JSON.stringify(raw)
    if (this.state === SOCKET_STATE.OPEN && this.socket) {
      return this._sendThroughSocket(payload, false)
    }

    if (this.state === SOCKET_STATE.CONNECTING || this.state === SOCKET_STATE.RECONNECTING || this._shouldAutoReconnect()) {
      if (this.pendingQueue.length >= this.config.maxQueueLength) {
        const dropped = this.pendingQueue.shift()
        this.diagnostics.warn('queue.drop', {
          reason: 'overflow',
          maxQueueLength: this.config.maxQueueLength,
          droppedSize: dropped?.length
        })
        this._notifySnapshot('queue.drop', {
          reason: 'overflow',
          length: this.pendingQueue.length
        })
      }
      this.pendingQueue.push(payload)
      this.diagnostics.debug('queue.enqueue', {
        state: this.state,
        length: this.pendingQueue.length,
        size: payload.length
      })
      this._notifySnapshot('queue.enqueue', {
        state: this.state,
        length: this.pendingQueue.length
      })
      if (!this.isConnected() && !this.connectPromise) {
        this.connect().catch((error) => {
          this.diagnostics.error('queue.connectFailed', { error })
          this._notifySnapshot('queue.connectFailed', { error })
        })
      }
      return false
    }

    this.diagnostics.warn('message.drop', { state: this.state })
    this._notifySnapshot('message.drop', { state: this.state })
    return false
  }

  /**
   * 注册 socket open 事件。
   * @param {Function} handler
   * @returns {Function} 解绑函数
   */
  onOpen(handler) {
    return this._register('open', handler)
  }

  /**
   * 注册 socket close 事件。
   * @param {Function} handler
   * @returns {Function} 解绑函数
   */
  onClose(handler) {
    return this._register('close', handler)
  }

  /**
   * 注册 socket 错误事件。
   * @param {Function} handler
   * @returns {Function} 解绑函数
   */
  onError(handler) {
    return this._register('error', handler)
  }

  /**
   * 注册消息事件（过滤掉 pong）。
   * @param {Function} handler
   * @returns {Function} 解绑函数
   */
  onMessage(handler) {
    return this._register('message', handler)
  }

  /**
   * 注册连接状态变化事件。
   * @param {Function} handler
   * @returns {Function} 解绑函数
   */
  onStateChange(handler) {
    return this._register('statechange', handler)
  }

  /**
   * 注册重连调度事件。
   * @param {Function} handler
   * @returns {Function} 解绑函数
   */
  onReconnecting(handler) {
    return this._register('reconnecting', handler)
  }

  /**
   * 注册重连成功事件。
   * @param {Function} handler
   * @returns {Function} 解绑函数
   */
  onReconnect(handler) {
    return this._register('reconnect', handler)
  }

  /**
   * 注册快照事件。
   * 仅在组件需要响应式状态时订阅。
   * @param {Function} handler
   * @returns {Function} 解绑函数
   */
  onSnapshot(handler) {
    return this._register('snapshot', handler)
  }

  offOpen(handler) {
    this._unregister('open', handler)
  }

  offClose(handler) {
    this._unregister('close', handler)
  }

  offError(handler) {
    this._unregister('error', handler)
  }

  offMessage(handler) {
    this._unregister('message', handler)
  }

  offStateChange(handler) {
    this._unregister('statechange', handler)
  }

  offReconnecting(handler) {
    this._unregister('reconnecting', handler)
  }

  offReconnect(handler) {
    this._unregister('reconnect', handler)
  }

  /**
   * 取消快照事件。
   * @param {Function} handler
   */
  offSnapshot(handler) {
    this._unregister('snapshot', handler)
  }

  /**
   * 设置是否自动重连。
   * @param {boolean} enabled
   */
  setAutoReconnect(enabled) {
    this.config.autoReconnect = Boolean(enabled)
    this.diagnostics.info('config.autoReconnect', { enabled: this.config.autoReconnect })
    this._notifySnapshot('config.autoReconnect', { enabled: this.config.autoReconnect })
  }

  /**
   * 调整心跳参数。
   * @param {object} options
   * @param {number} [options.heartbeatInterval]
   * @param {number} [options.heartbeatTimeout]
   */
  setHeartbeat(options = {}) {
    const { heartbeatInterval, heartbeatTimeout } = options
    if (typeof heartbeatInterval === 'number') {
      this.config.heartbeatInterval = heartbeatInterval
    }
    if (typeof heartbeatTimeout === 'number') {
      this.config.heartbeatTimeout = heartbeatTimeout
    }
    this.diagnostics.info('config.heartbeat', {
      heartbeatInterval: this.config.heartbeatInterval,
      heartbeatTimeout: this.config.heartbeatTimeout
    })
    if (this.isConnected()) {
      this._startHeartbeat()
    }
    this._notifySnapshot('config.heartbeat', {
      heartbeatInterval: this.config.heartbeatInterval,
      heartbeatTimeout: this.config.heartbeatTimeout
    })
  }

  setDiagnostics(channel) {
    if (channel) {
      this.diagnostics = channel
    }
  }

  /**
   * 判断当前是否处于连接状态。
   * @returns {boolean}
   */
  isConnected() {
    return this.state === SOCKET_STATE.OPEN && !!this.socket
  }

  /**
   * 获取最新快照。
   * @returns {object} socket 状态摘要
   */
  getSnapshot() {
    return {
      state: this.state,
      connected: this.isConnected(),
      retryCount: this.retryCount,
      lastSeen: this.lastSeen,
      pendingQueueLength: this.pendingQueue.length,
      autoReconnect: this.config.autoReconnect,
      connectTimeout: this.config.connectTimeout,
      heartbeatInterval: this.config.heartbeatInterval,
      heartbeatTimeout: this.config.heartbeatTimeout,
      intentionalClose: this.intentionalClose,
      reconnectTimerActive: Boolean(this.reconnectTimer),
      connectTimeoutTimerActive: Boolean(this.connectTimeoutTimer),
      heartbeatTimerActive: Boolean(this.heartbeatTimer),
      timestamp: now()
    }
  }

  _sendThroughSocket(payload, fromQueue) {
    if (!this.socket) return false
    let sent = true
    try {
      this.socket.send(payload)
      this.lastSeen = now()
      this.diagnostics.debug('message.out', { queued: fromQueue, payload: payload === 'ping' ? payload : JSON.parse(payload ?? '{}') })
      this._notifySnapshot('message.sent', { queued: fromQueue, payload })
    } catch (error) {
      sent = false
      this.diagnostics.error('message.sendException', { error })
      this._emit('error', error)
      this._notifySnapshot('message.sendException', { error })
    }
    return sent
  }

  _startHeartbeat() {
    this._stopHeartbeat()
    if (!this.config.heartbeatInterval) return

    this.heartbeatTimer = setInterval(() => {
      if (!this.socket) return

      const elapsed = now() - this.lastSeen
      if (elapsed >= this.config.heartbeatTimeout) {
        this.diagnostics.warn('heartbeat.timeout', { elapsed, timeout: this.config.heartbeatTimeout })
        try {
          this.socket.close(4000, 'Heartbeat timeout')
        } catch (error) {
          this.diagnostics.error('heartbeat.closeError', { error })
        }
        return
      }

      try {
        this.socket.send('ping')
        this.diagnostics.debug('heartbeat.ping', { elapsed })
      } catch (error) {
        this.diagnostics.error('heartbeat.sendError', { error })
        try {
          this.socket.close()
        } catch (closeError) {
          this.diagnostics.error('heartbeat.forceCloseError', { error: closeError })
        }
      }
    }, this.config.heartbeatInterval)
    this._notifySnapshot('heartbeat.start')
  }

  _stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
      this.diagnostics.debug('heartbeat.stop')
      this._notifySnapshot('heartbeat.stop')
    }
  }

  _startConnectTimeout() {
    this._clearConnectTimeout()
    if (!this.config.connectTimeout) return

    this.connectTimeoutTimer = setTimeout(() => {
      const timeout = this.config.connectTimeout
      const error = new Error('连接超时')
      error.code = 'CONNECT_TIMEOUT'
      this.diagnostics.warn('connect.timeout', { timeout })
      this._emit('error', error)
      this._notifySnapshot('connect.timeout', { timeout })
      try {
        this.socket?.close(4008, 'connect timeout')
      } catch (closeError) {
        this.diagnostics.error('connect.timeoutForceCloseFailed', { error: closeError })
      }
    }, this.config.connectTimeout)
  }

  _clearConnectTimeout() {
    if (this.connectTimeoutTimer) {
      clearTimeout(this.connectTimeoutTimer)
      this.connectTimeoutTimer = null
      this.diagnostics.debug('connect.timeout.clear')
      this._notifySnapshot('connect.timeout.clear')
    }
  }

  _scheduleReconnect() {
    this._clearReconnectTimer()
    if (!this._shouldAutoReconnect()) return
    if (this.usesDefaultUrlBuilder && !getAccessToken()) {
      this.diagnostics.warn('reconnect.skipNoToken', { attempt: this.retryCount })
      this.retryCount = 0
      this._setState(SOCKET_STATE.IDLE, { reason: 'noToken' })
      return
    }

    this.retryCount += 1
    const delay = backoffDelay(this.retryCount)

    this._setState(SOCKET_STATE.RECONNECTING, { attempt: this.retryCount, delay })
    this.diagnostics.warn('reconnect.schedule', { attempt: this.retryCount, delay })
    this._emit('reconnecting', { attempt: this.retryCount, delay })

    this.reconnectTimer = setTimeout(async () => {
      this.diagnostics.info('reconnect.attempt', { attempt: this.retryCount })
      try {
        await this.connect(true)
      } catch (error) {
        this.diagnostics.error('reconnect.failed', { error, attempt: this.retryCount })
      }
    }, delay)
    this._notifySnapshot('reconnect.schedule', { delay, attempt: this.retryCount })
  }

  _flushQueue() {
    if (!this.isConnected() || this.pendingQueue.length === 0) return

    const flushed = []
    while (this.pendingQueue.length > 0) {
      const payload = this.pendingQueue.shift()
      const sent = this._sendThroughSocket(payload, true)
      if (sent) {
        flushed.push(payload.length)
      } else {
        this.pendingQueue.unshift(payload)
        break
      }
    }

    if (flushed.length > 0) {
      this.diagnostics.debug('queue.flush', {
        count: flushed.length,
        sizes: flushed.slice(0, 10),
        remaining: this.pendingQueue.length
      })
    }
    if (flushed.length > 0) {
      this._notifySnapshot('queue.flush', { flushed: flushed.length })
    }
  }

  _clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
      this.diagnostics.debug('reconnect.cancel')
      this._notifySnapshot('reconnect.cancel')
    }
  }

  _register(event, handler) {
    this.listeners[event].add(handler)
    return () => this._unregister(event, handler)
  }

  _unregister(event, handler) {
    this.listeners[event].delete(handler)
  }

  _emit(event, payload) {
    this.listeners[event].forEach((handler) => {
      try {
        handler(payload)
      } catch (error) {
        this.diagnostics.error('event.handlerError', { event, error })
      }
    })
  }

  _setState(nextState, context = {}) {
    if (this.state === nextState) return
    const prevState = this.state
    this.state = nextState
    this.diagnostics.debug('state.change', { prevState, nextState, ...context })
    this._emit('statechange', this.state)
    this._notifySnapshot('state.change', { prevState, nextState, context })
  }

  _shouldAutoReconnect() {
    return Boolean(this.config.autoReconnect)
  }

  _notifySnapshot(reason, extra = {}) {
    if (!this.listeners.snapshot || this.listeners.snapshot.size === 0) return
    const payload = {
      reason,
      snapshot: this.getSnapshot(),
      ...extra
    }
    this._emit('snapshot', payload)
  }
}

const socketClient = new SocketClient({ diagnostics: diagnosticsManager.createChannel('socket') })

export { SocketClient, SOCKET_STATE, socketClient }
export default socketClient
